import React from 'react'
import { useHasFeature, withFeatures } from 'features'
import { usePermission } from 'admin/hocs'
import { getProductOptions, formatFlatOptions } from 'uiComponents/filter/filterHelpers'
import { MetaData, FilterCategory } from 'uiComponents/filter/schema'
import Filter from 'uiComponents/filter'
import { getStaticMetadaCategories, applicableFilters } from './config'
import { useTranslation } from 'translations'
import { useGetFullProductsAndArticlesNestedTree } from 'products/redux'
import { useGetAccountLocalesQuery, useGetAccountLocationsQuery, useGetResellerListQuery } from 'orders/reduxQueries'
import { ResellerResponseItem } from 'channels/channelsService'

interface OrdersFilterProps {
    page: 'orders' | 'tickets'
    accountSlug: string
    hasFeature: (feature: string, accountSlug: string) => boolean
}

const OrdersDynamicFilter: React.FC<OrdersFilterProps> = (props) => {
    const { t } = useTranslation()
    const { hasPermission } = usePermission()
    const isAdmin = hasPermission('partner_admin', props.accountSlug)
    const { products, isLoading } = useGetFullProductsAndArticlesNestedTree()
    const locationQuery = useGetAccountLocationsQuery({ accountSlug: props.accountSlug })
    const localesQuery = useGetAccountLocalesQuery({ accountSlug: props.accountSlug })
    const channelsQuery = useGetResellerListQuery({ accountSlug: props.accountSlug, query: '?include_archived=true' })
    const useComputedOrderStatus = useHasFeature('use_computed_order_status')

    const data = React.useMemo(() => {
        const productCategory = {
            category: 'products' as FilterCategory,
            options: getProductOptions(products),
        }

        const locationCategory = {
            category: 'location_id' as FilterCategory,
            options: formatFlatOptions(
                'location_id',
                (locationQuery.data ?? []).map((l) => ({
                    name: l.name,
                    slug: l.uuid,
                })),
            ),
        }

        const languageCategory = {
            category: 'language' as FilterCategory,
            options: formatFlatOptions(
                'language',
                (localesQuery.data?.locales ?? []).map((l) => ({ name: l.name, slug: l.code })),
            ),
        }

        const channelsCategory = ((resellers: ResellerResponseItem[]) => {
            const mapResellerOptions = (resellers: ResellerResponseItem[]) => {
                return resellers.map((r) => ({
                    name: r.name,
                    slug: r.resellerId,
                    category: 'channels' as FilterCategory,
                    parents: ['all_resellers'],
                    children: [],
                    leafNode: true,
                }))
            }

            const options =
                resellers.length > 0
                    ? [
                          {
                              name: t('orders.orders.filters.directSales'),
                              slug: 'direct_sales',
                              category: 'channels' as FilterCategory,
                              parents: [],
                              children: [],
                              leafNode: true,
                          },
                          {
                              name: t('orders.orders.filters.allResellers'),
                              slug: 'all_resellers',
                              category: 'channels' as FilterCategory,
                              parents: [],
                              children: mapResellerOptions(resellers),
                              leafNode: false,
                          },
                      ]
                    : []
            return {
                category: 'channels' as FilterCategory,
                options,
            }
        })(isAdmin && channelsQuery.data ? channelsQuery.data : [])

        const categories: MetaData[] = [
            ...getStaticMetadaCategories(),
            productCategory,
            locationCategory,
            languageCategory,
            channelsCategory,
        ]

        if (!useComputedOrderStatus) {
            categories.map((cat) => {
                if (cat.category === 'status') {
                    cat.options = cat.options.filter((option) => ['OPEN', 'CONFIRMED', 'CANCELED'].includes(option.slug))
                }
            })
        }

        const categoriesInOrder: MetaData[] = []
        applicableFilters[props.page].forEach((f) => {
            const category = categories.find((c) => c.category === f)
            if (!!category) {
                categoriesInOrder.push(category)
            }
        })
        return categoriesInOrder
    }, [products, locationQuery.data, localesQuery.data, channelsQuery.data, isAdmin, props.page])

    const getApplicableFilters = React.useCallback(() => {
        const filtersToHide: FilterCategory[] = []
        if (
            !hasPermission('partner_admin', props.accountSlug) &&
            !props.hasFeature('ResellersFeature', props.accountSlug)
        ) {
            filtersToHide.push('channels')
        }
        return applicableFilters[props.page].filter((f) => filtersToHide.indexOf(f) < 0)
    }, [hasPermission, props.hasFeature, props.page])

    return (
        <Filter
            accountSlug={props.accountSlug}
            compact
            metaData={data}
            applicableFilters={getApplicableFilters()}
            standaloneFilters={[]}
            singleSelectCategories={[]}
            loading={locationQuery.isLoading || localesQuery.isLoading || channelsQuery.isLoading || isLoading}
        />
    )
}

export default withFeatures(OrdersDynamicFilter)
